import { useRef, useEffect, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const areas = [
  {
    title: 'Criminal Defense',
    desc: 'Aggressive defense for misdemeanor and felony charges — drug crimes, federal defense, DUI, and expungements. We fight to protect your freedom and future in Santa Maria and across the Central Coast.',
    icon: 'ri-shield-keyhole-fill',
    img: 'https://public.readdy.ai/ai/img_res/2b8a9b4d1e3823b630ecafb0679d9f37.jpg',
    tag: 'Most Requested',
  },
  {
    title: 'Personal Injury',
    desc: 'Maximum compensation for auto accidents, wrongful death, bicycle accidents, and catastrophic injuries. We fight relentlessly for what you deserve — no recovery, no fee.',
    icon: 'ri-heart-pulse-fill',
    img: 'https://readdy.ai/api/search-image?query=professional%20legal%20consultation%20office%20with%20dark%20wood%20desk%2C%20legal%20documents%2C%20warm%20lamp%20light%2C%20leather%20chair%2C%20law%20books%20in%20background%2C%20moody%20atmospheric%20lighting%2C%20deep%20shadows%2C%20cinematic%20quality%2C%20no%20people%2C%20dark%20charcoal%20tones&width=800&height=600&seq=practice-injury-001&orientation=landscape',
    tag: null,
  },
  {
    title: 'Family Law',
    desc: 'Compassionate yet firm representation in divorce, child custody, spousal support, and property distribution. We protect what matters most — your family and your future.',
    icon: 'ri-home-heart-fill',
    img: 'https://readdy.ai/api/search-image?query=elegant%20law%20office%20interior%20with%20bookshelves%20filled%20with%20legal%20volumes%2C%20dark%20mahogany%20furniture%2C%20soft%20warm%20lighting%2C%20sophisticated%20atmosphere%2C%20cinematic%20depth%2C%20no%20people%2C%20dark%20background%20with%20golden%20light%20accents%2C%20high%20end%20professional%20setting&width=800&height=600&seq=practice-family-001&orientation=landscape',
    tag: null,
  },
  {
    title: 'Civil Litigation',
    desc: 'Strategic representation in complex civil disputes, business conflicts, and collections matters. We build strong legal positions and litigate with precision to achieve favorable outcomes.',
    icon: 'ri-scales-3-fill',
    img: 'https://readdy.ai/api/search-image?query=dramatic%20legal%20scales%20of%20justice%20on%20dark%20wood%20desk%20with%20law%20books%2C%20gavel%2C%20moody%20cinematic%20lighting%2C%20deep%20shadows%2C%20warm%20amber%20glow%2C%20dark%20charcoal%20background%2C%20high%20contrast%20editorial%20photography%2C%20no%20people%2C%20luxury%20legal%20setting&width=800&height=600&seq=practice-civil-001&orientation=landscape',
    tag: null,
  },
];

export default function PracticeAreas() {
  const { ref, inView } = useInView();

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="practice-areas" className="bg-[#F7F5F0] py-24 md:py-32" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">

        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-[2px] bg-[#C9A84C]"></div>
              <span className="text-[#C9A84C] text-[10px] tracking-[0.45em] uppercase font-medium">What We Handle</span>
            </div>
            <h2 className="font-serif text-[#0D0D0D] text-4xl md:text-5xl font-bold leading-[1.1]">
              Focused Legal<br />Expertise
            </h2>
          </div>
          <p className="text-[#777] text-base max-w-sm leading-relaxed md:text-right">
            Comprehensive representation across the areas that matter most — delivered with precision, urgency, and a relentless focus on results.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {areas.map((area, i) => (
            <div
              key={i}
              className={`group relative overflow-hidden cursor-pointer transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${i * 100}ms`, height: i < 2 ? '400px' : '320px' }}
              onClick={scrollToContact}
            >
              {/* Image */}
              <img
                src={area.img}
                alt={`${area.title} — Gamble & Garcia Attorneys at Law`}
                className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-black/15 group-hover:from-black/98 transition-all duration-500"></div>
              {/* Gold left border reveal */}
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#C9A84C] scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-bottom"></div>

              {/* Tag */}
              {area.tag && (
                <div className="absolute top-5 right-5 px-3 py-1.5 bg-[#C9A84C] text-black text-[9px] font-bold tracking-[0.3em] uppercase">
                  {area.tag}
                </div>
              )}

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${area.icon} text-[#C9A84C] text-xl`}></i>
                  </div>
                  <h3 className="font-serif text-white text-xl font-bold">{area.title}</h3>
                </div>
                <p className="text-white/60 text-sm leading-relaxed max-w-sm">{area.desc}</p>
                <div className="mt-5 flex items-center gap-2 text-[#C9A84C] text-[11px] tracking-[0.22em] uppercase font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>Discuss Your Case</span>
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className={`mt-10 flex flex-col sm:flex-row items-center justify-between gap-6 p-8 md:p-10 bg-[#0D0D0D] transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <p className="text-white font-serif text-xl md:text-2xl font-bold leading-snug">Not sure which area applies to your situation?</p>
            <p className="text-white/40 text-sm mt-1.5">We&apos;ll evaluate your case and point you in the right direction — at no cost.</p>
          </div>
          <button
            onClick={scrollToContact}
            className="flex-shrink-0 px-8 py-4 bg-[#C9A84C] text-black font-bold text-[11px] tracking-[0.25em] uppercase hover:bg-[#b8943e] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Speak With an Attorney Today
          </button>
        </div>
      </div>
    </section>
  );
}
